import { useState } from "react";
import { Outlet, useLocation } from "react-router";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth/use-auth";
import { useLiveUpdates } from "@/features/notifications/use-live-updates";
import { AssistantProvider } from "@/features/assistant/assistant-provider";
import { AppSidebar } from "./app-sidebar";
import { AppHeader } from "./app-header";
import { CompaniesLimitBanner } from "./companies-limit-banner";
import { TrialBanner } from "./trial-banner";
import { AppFooter } from "./app-footer";
import { ImpersonationBanner } from "./impersonation-banner";

// The sidebar sits on the paper background; the page is one white sheet laid
// on it (framed and lifted on md+, edge to edge on a phone); white panels sit a
// step above the sheet. Header and content share the sheet.

// The sidebar writes its open state to a cookie; v1 kept it in localStorage
function sidebarStartsOpen() {
  const cookie = document.cookie.match(/(?:^|; )sidebar_state=(true|false)/)
  if (cookie) return cookie[1] === "true";
  try {
    const v1 = JSON.parse(localStorage.getItem("sidebarState") ?? "null");
    if (typeof v1?.opened === "boolean") return v1.opened;
  } catch {
    /* ignore */
  }
  return true;
}

export function AppLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  useLiveUpdates(user?.uid);
  const [startOpen] = useState(sidebarStartsOpen);

  return (
    <AssistantProvider>
      <SidebarProvider className="bg-sidebar" defaultOpen={startOpen}>
        <AppSidebar />
        <SidebarInset className="min-w-0 bg-sheet md:ring-1 md:ring-(--panel-edge) md:peer-data-[variant=inset]:shadow-md">
          <ImpersonationBanner />
          <AppHeader />
          <TrialBanner />
          <CompaniesLimitBanner />
          {/* keyed by path so each page eases in instead of snapping */}
          <div
            key={pathname}
            className="mx-auto w-full max-w-7xl flex-1 animate-in fade-in-0 slide-in-from-bottom-1 px-4 py-6 duration-300 ease-out motion-reduce:animate-none md:px-8 md:py-8"
          >
            <Outlet />
          </div>
          <AppFooter />
        </SidebarInset>
      </SidebarProvider>
    </AssistantProvider>
  );
}
